/*
* Contains all the components for displaying and changing payment plans
List of possible payment plans are in the FAQ.md
*/

import * as React from "react";
import constants from "../../constants";
import MainMenu from "../menu/MainMenu";
import { GFUser, GFPlan, GFInitialState } from "../../models/models";
import { connect } from "react-redux";
import {
  manualAjaxEnd,
  manualAjaxStart,
} from "../../actions/ajaxStatusActions";
import VerificationAlert from "../common/VerificationAlert";
import * as moment from "moment";

import {
  cancelSubscription,
  couponCheck,
  updateCard,
} from "../../actions/paymentActions";
import { toastr } from "react-redux-toastr";
import {
  Grid,
  Row,
  Col,
  ListGroup,
  ListGroupItem,
  Modal,
} from "react-bootstrap";
import PaymentButton from "./PaymentButton";
import UpdateCardSection from "./UpdateCardSection";
import { ManageAccountButtons } from "./ManageAccountButtons";
import { selectIsTeacher, hasSubscription, hasStripeSubscription, selectHasGoogleClasses } from "../../reducers/userReducer";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import CheckoutModal from "./CheckoutModal";
import PlanSelector from './PlanSelector';
import utils from './utils';
import resolveDomain from '../../resolve-domain';
import { checkSession } from "../../actions/userActions";
import AvailableContentLeft from './AvailableContentLeft';
import AvailableContentRight from './AvailableContentRight';

const graham = require("../../images/graham.gif");
const masterGraham = require("../../images/diploma.png");
interface Props extends React.Props<Checkout> {
  user: GFUser;
  loading: boolean;
  cancelSubscription: () => Promise<void>;
  updateCard: (paymentToken: string) => Promise<void>;
  manualAjaxEnd: any;
  manualAjaxStart: any;
  hasGoogleClasses: boolean;
  isTeacher: boolean;
  stripe: Stripe;
  elements: StripeElements;
  checkSession: typeof checkSession;
}

interface State {
  planRecurringType: string;
  StripeKey: string;
  newPaymentPlan: string;
  paymentPlanStudents: number;
  showPaymentModal: boolean;
  masterButton: string;
  showCreditCardModal: boolean;
  selectedPlan: GFPlan;
}

class Checkout extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      planRecurringType: "Annual",
      StripeKey: resolveDomain().stripe,
      newPaymentPlan: "",
      paymentPlanStudents: 25,
      showPaymentModal: false,
      masterButton: "",
      showCreditCardModal: false,
      selectedPlan: { ...constants.paymentPlanObjectsByID.masterAnnual_3 },
    };
  }
  componentDidMount(){
    if (this.props.isTeacher) {
      this.props.checkSession();
    }

    if(this.props.user.paymentPlan){
      this.updateSelectedPaymentPlan(this.props.user)
    }
  }

  cancelSubscription = () => {
    this.props
      .cancelSubscription()
      .then((user) => {
        toastr.success(
          "Success",
          "You have successfully cancelled your subscription.",
          constants.toastrSuccess
        );
      })
      .catch((error) => {
        console.error("Unable to delete subscription", error);
        const message = "cancel subscription";
        constants.handleError(error, message);
      });
  };

  showConfirmationModal = () => {
    const toastrConfirmOptions = {
      onOk: () => {
        this.cancelSubscription();
      },
      onCancel: () => console.log("CANCEL: clicked"),
      okText: "Yes, remove me.",
      cancelText: "No, I want to stay.",
    };
    toastr.confirm(
      "Are you sure you want to cancel your subscription?",
      toastrConfirmOptions
    );
  };

  openUpdateCreditCard = () => {
    this.setState({ showCreditCardModal: true });
  };

  closeUpdateCreditCard = () => {
    this.setState({ showCreditCardModal: false });
  };

  closePaymentModal = () => {
    this.setState({ showPaymentModal: false });
  };

  openPaymentModal = () => {
      this.setState({ showPaymentModal: true });
  };
  handleChange = (e: any) => {
    if (e) {
      e.preventDefault();
    }

    const targetName = e.currentTarget.name;
    this.setState({ [targetName]: e.currentTarget.value } as unknown as State, () => {
      this.updateSelectedPaymentPlan();
    });
  };

  togglePlanRecurringType = () => {
    this.setState(
      { planRecurringType: this.state.planRecurringType === "Annual" ? "Monthly" : "Annual" },
      () => {
        this.updateSelectedPaymentPlan();
      }
    );
  };

  /*
   * Update Credit Card Modal
   */
  updateCreditCardModal = () => {
    return (
      <Modal
        show={this.state.showCreditCardModal}
        onHide={this.closeUpdateCreditCard}
      >
        <Modal.Header closeButton={true}>
          <Modal.Title>Update Credit Card</Modal.Title>
        </Modal.Header>
        <UpdateCardSection
          user={this.props.user}
          loading={this.props.loading}
          show={this.state.showCreditCardModal}
          hide={this.closeUpdateCreditCard}
          updateCard={this.props.updateCard}
          manualAjaxStart={this.props.manualAjaxStart}
          manualAjaxEnd={this.props.manualAjaxEnd}
          stripe={this.props.stripe}
          elements={this.props.elements}
        />
      </Modal>
    );
  };

  displayExpirationDate = () => {
    const { user } = this.props;
    const showDate = moment
      .utc(user.expirationDate)
      .local(true)
      .format("MMMM Do, YYYY");
    let message;

    if (user.isActive) {
      if (user.isCancelling) {
        message = "Cancellation pending.  Account Expires on";
      } else if (user.paymentPlan === "trial") {
        message = "Trial Account Expires on";
      } else if (user.isPurchaseOrder === true) {
        message = "Account expires on"; // purchase orders do not automatically renew
      } else {
        message = "Account renews on";
      }
      if (user.isPurchaseOrder) {
        message = `This is a purchase order subscription with ${user.maxStudents} student seats.  ${message}`;
      }
    } else {
      message = "Account Expired on";
    }

    return (
      <div>
        {message} {showDate}
      </div>
    );
  };

  /*
   * updateSelectedPaymentPlan
   * figure out the selectedPayment plan and set it to state
   */
  updateSelectedPaymentPlan = (user?: GFUser) => {
    const selectedPlan = utils.selectPaymentPlan(this.state.planRecurringType, this.state.paymentPlanStudents, user);

    this.setState({
      selectedPlan: selectedPlan.plan,
      planRecurringType: selectedPlan.plan.time,
    });

    if (selectedPlan.overridePaymentPlanStudents) {
      this.setState({
        paymentPlanStudents: selectedPlan.plan.size * selectedPlan.plan.quantity,
      });
    }
  };

  isHomeschoolMonthly = () => {
    return false;
  };

  render() {
    const { isTeacher } = this.props;

    let amountOfStudents = this.state.selectedPlan.size * this.state.selectedPlan.quantity;

    const subscription = hasSubscription(this.props.user);
    const stripeSubscription = hasStripeSubscription(this.props.user);

    const planChange = utils.planChange(this.props.user, this.state.selectedPlan, stripeSubscription);

    const totalPrice = utils.totalPrice(planChange.isCurrent ? planChange.userCurrentPlan : this.state.selectedPlan);

    // TODO can we simply change the paymentPlans time attribute to "Annually"

    return (
      <div>
        <Grid className="content modal-container">
          <div className="sidemenu">
            <MainMenu/>
            <div className="logo text-center">
              <img alt="graham" src={graham} />
            </div>
          </div>
          <div className="main-content content-with-sidebar payment">
            <VerificationAlert user={this.props.user} isTeacher={isTeacher} />
            <Row>
              <Col md={12} xs={12}>
                <div className="payment-header">
                  <h1>Payment and Plans</h1>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={12} xs={12}>
                <div className="link-to-other-class-numbers text-center">
                  <h3>{this.displayExpirationDate()}</h3>
                </div>
              </Col>
            </Row>
            <Row className="display-payment-options">
              {/*
               * Display Payment Modal
               */}
              <CheckoutModal
                show={this.state.showPaymentModal}
                stripe={this.props.stripe}
                selectedPlan={this.state.selectedPlan}
                hide={this.closePaymentModal}
                elements={this.props.elements}
                togglePlanRecurringType={this.togglePlanRecurringType}
              />
              {/*
               * Display Update Credit Card Modal
               */
              this.updateCreditCardModal()}

              <Col md={5} xs={5} style={{ padding: 0 }}>
                <div className="payment-col-1">
                  {! this.props.user.isGroup && <>
                    <div className="top-box">
                      <PlanSelector
                        handleChange={this.handleChange}
                        paymentPlanStudents={this.state.paymentPlanStudents}
                        planRecurringType={this.state.planRecurringType}
                        selectedPlan={this.state.selectedPlan}
                        loading={this.props.loading}
                      />
                      <ManageAccountButtons
                        hasSubscription={subscription}
                        openUpdateCreditCard={this.openUpdateCreditCard}
                        showConfirmationModal={this.showConfirmationModal}
                        openPaymentModal={this.openPaymentModal}
                        isPurchaseOrder={this.props.user.isPurchaseOrder}
                      />
                    </div>
                  </>}
                  <AvailableContentLeft/>
                </div>
              </Col>
              <Col md={7} xs={7} style={{ padding: 0 }}>
                {this.isHomeschoolMonthly() && (
                  <h3 style={{ padding: "20px" }}>
                    Homeschool plans are annual only.  Please select annual.
                  </h3>
                )}
                  <div className="payment-col-2">
                    {! this.props.user.isGroup && <>
                      <div className="top-box">
                        <Row>
                          <img alt="master graham" src={masterGraham} />
                        </Row>
                        <Row>
                          <h1 className="col-2">GrammarFlip Subscription</h1>
                          <h5 className="display-amount-of-students">
                            {amountOfStudents} Students
                          </h5>
                          <h3 style={{ fontWeight: 600, marginTop: "10px" }}>
                            ${totalPrice} /
                            {this.state.planRecurringType}
                          </h3>
                        </Row>
                        <Row className="payment-button">
                          <PaymentButton
                            planChange={planChange}
                            handleClick={this.openPaymentModal}
                            loading={this.props.loading}
                          />
                        </Row>
                      </div>
                    </>}
                    <AvailableContentRight/>
                  </div>
              </Col>
            </Row>
          </div>
        </Grid>
      </div>
    );
  }
}
const mapStateToProps = (state: GFInitialState, ownProps: any) => {
  return {
    user: state.user,
    loading: state.ajaxCallsInProgress > 0,
    isTeacher: selectIsTeacher(state),
    hasGoogleClasses: selectHasGoogleClasses(state),
  };
};

const myCheckoutForm = connect(mapStateToProps, {
  cancelSubscription,
  couponCheck,
  updateCard,
  manualAjaxEnd,
  manualAjaxStart,
  checkSession,
})(Checkout);
// export default injectStripe(myCheckoutForm);
export default myCheckoutForm;
